import React from 'react';
import { useDropzone } from 'react-dropzone';
import { useFormContext } from 'react-hook-form';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { Icon } from '../../../../CommonComponents/OPL/Icon/Icon';
import { MESSAGES } from '../../../../CommonConstants';
import { MAX_407_LETTER_SIZE } from './Letter407Upload.meta';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import { useUpload407LetterMutation } from '../../api/useUpload407LetterMutation';
import { useDocumentMetaDataQuery } from '../../api/useDocumentMetaDataQuery';
import { useRouteParams } from '../../hooks/useRouteParams';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { IconButton } from '../../../../CommonComponents/OPL/IconButton/IconButton';
import { HelperText } from '../../../../CommonComponents/OPL/HelperText/HelperText';
import { truncateFileName } from '../../../../CommonUtils/truncateFileName';

interface Props {
  isReadonly?: boolean;
  isReview?: boolean;
}

export const Letter407Upload: React.FC<Props> = ({ isReadonly, isReview }) => {
  const formBag = useFormContext();
  const {
    formState: { errors },
  } = formBag;

  const { clientId, draftId } = useRouteParams();

  const [isAffiliatedExchangeOrFINRA, snapId] = formBag.watch([
    'isAffiliatedExchangeOrFINRA',
    'snapId',
  ]);

  const {
    isFetching: isGettingDocumentMetadata,
    isError: isDocumentMetaDataError,
    refetch,
    data,
  } = useDocumentMetaDataQuery({
    clientId,
    draftId,
    snapId,
    isAffiliatedExchangeOrFINRA,
  });

  const {
    mutateAsync: upload407Letter,
    isLoading: isUploading,
    isError: isUploadError,
  } = useUpload407LetterMutation();

  const { getInputProps, open } = useDropzone({
    accept: 'image/*,application/pdf',
    multiple: false,
    noDrag: true,
    maxSize: MAX_407_LETTER_SIZE * 1e6,

    onDropAccepted: async ([file]) => {
      try {
        const response = await upload407Letter(file);

        formBag.setValue('snapId', response.snapId);
      } catch (err) {
        console.error(err);
      }
    },

    onDropRejected([{ file }]) {
      if (file.size > MAX_407_LETTER_SIZE * 1e6) {
        toast.error(`The file size must be less than ${MAX_407_LETTER_SIZE}MB.`);
        return;
      }

      toast.error(MESSAGES.GENERIC_ERROR);
    },
  });

  if (!isAffiliatedExchangeOrFINRA) {
    return null;
  }

  const downloadDocument = () => {
    window.open(
      `/Consumer/ManagedAccounts/DownloadDocument?clientId=${clientId}&id=${draftId}&snapId=${snapId}`,
      '_blank',
    );
  };

  const renderContent = () => {
    if (isGettingDocumentMetadata || isUploading) {
      return (
        <Text color="neutral.500" fontSize="small">
          Loading...
        </Text>
      );
    }

    if (isUploadError || isDocumentMetaDataError) {
      return (
        <Box display="flex" alignItems="center">
          <Text color="error.500">{isUploadError ? 'Upload failed' : 'Something went wrong'}</Text>

          <Button
            variant="link"
            onClick={() => {
              if (isUploadError) {
                open();
                return;
              }

              refetch();
            }}
            ml="4"
          >
            Try again
          </Button>
        </Box>
      );
    }

    return snapId && data ? (
      <Box display="flex" alignItems="center">
        <Box flex={1} display="flex" alignItems="center" mr="16">
          <Icon className="far fa-file-alt" color="neutral.500" mr="4" />

          <Button variant="link" onClick={downloadDocument}>
            {truncateFileName(data.fileName, isReview ? 25 : 50)}
          </Button>

          <Icon className="fas fa-check-circle" color="success.500" ml="8" />
        </Box>

        {!isReadonly && (
          <IconButton
            icon="far fa-pencil"
            variant="subtleInactive"
            aria-label="Edit 407 letter"
            onClick={open}
          />
        )}
      </Box>
    ) : (
      <>
        <Text color="neutral.500" fontSize="small">
          To open this account, you&apos;ll need to upload a 407 Letter.{' '}
          <Button {...formBag.register('snapId')} variant="link" size="small" onClick={open}>
            Upload now
          </Button>{' '}
          (Format: .PDF .PNG .JPG)
        </Text>

        {errors.snapId ? (
          <HelperText variant="error" mt="4">
            {errors.snapId.message}
          </HelperText>
        ) : null}
      </>
    );
  };

  return (
    <>
      <input {...formBag.register('snapId')} type="hidden" />
      <input {...getInputProps()} />

      {renderContent()}
    </>
  );
};
